import { useFeatureIsOn } from '@growthbook/growthbook-react';
import React, { useEffect, useState } from 'react';
import { useSession } from '@/auth/index';
import {
  SmartSearchModal,
  SmartSearchV1Modal,
} from '@/components/SmartSearchModal';
import { useSmartSearchModal } from '@/components/SmartSearchModal/hooks/useSmartSearchModal';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  useSriracha,
  Notification,
  NormalizedNotification,
  normalizeNotificationObject,
  NotificationBell,
  NotificationDropdown,
} from '@/notifications/index';
import { SearchTabData } from '@/reports/index';
import { SmartSearchIcon } from '@/theme/customIcons/SmartSearchIcon';
import { Icons, Stack, Text } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { HeaderMenu } from './HeaderMenu/HeaderMenu';
import { MobileSearchInput } from './MobileSearchInput/MobileSearchInput';
import { SearchTab } from './SearchTab/SearchTab';
import { HeaderNav, TabText, HeaderNavContainer } from './components';
import { HeaderNavItem } from '../../components';

interface HeaderSearchProps {
  selectedTab: SearchTabData | null;
  setSelectedTab: (tab: SearchTabData) => void;
  toggleIsMounted: () => void;
  enableSmartSearch: boolean;
  shouldHideSearchForm?: boolean;
  isInternationalVinTest?: boolean;
  isInInternationalLanguageTest?: boolean;
}

export function HeaderSearch({
  setSelectedTab,
  selectedTab,
  toggleIsMounted,
  enableSmartSearch,
  shouldHideSearchForm,
  isInternationalVinTest,
  isInInternationalLanguageTest,
}: HeaderSearchProps) {
  const { trackEventGA4 } = useAppConfigContext();
  const {
    config: { searchTabs },
    featureFlags: { notificationsDisabled },
  } = useAppConstantsContext();
  const searchTabsReplacementText = null;
  const { isMedium } = useScreenSize();
  const { data, readNotification, readAllNotifications, deleteNotification } =
    useSriracha();
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [normalizedNotifications, setNormalizedNotifications] = useState<
    NormalizedNotification[]
  >([]);

  const isInSuggestedReportsControl = !useFeatureIsOn('oar-1064');

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLLIElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const [notificationdropdownAnchorEl, setNotificationDropdownAnchorEl] =
    useState<null | HTMLElement>(null);
  const openNotifications = Boolean(notificationdropdownAnchorEl);

  const handleNotificationBellClick = (
    event: React.MouseEvent<HTMLLIElement>
  ) => {
    trackEventGA4({
      event_name: 'feature_Engagement',
      type: 'view_notifications',
      interacted_from: 'notifications',
    });
    setNotificationDropdownAnchorEl(event.currentTarget);
  };
  const handleCloseNotificationDropdown = () => {
    setNotificationDropdownAnchorEl(null);
  };

  const onReadNotification = (id: number) => {
    readNotification.mutate(id);
  };

  const onReadAllNotifications = () => {
    readAllNotifications.mutate();
  };

  const onDeleteNotification = (id: number) => {
    deleteNotification.mutate(id);
  };

  const {
    session: { account },
  } = useSession();
  const isNonCompliant = account?.account.user_info.noncompliant;

  const isInSmartSearchVariation = useFeatureIsOn('prop-2164');
  const isInSmartSearchV1Variation = useFeatureIsOn('prop-2565');
  const enableSmartSearchTabInDesktopHeader =
    enableSmartSearch &&
    (isInSmartSearchVariation || isInSmartSearchV1Variation);
  const {
    isV1ModalOpen,
    openV1Modal,
    closeV1Modal,
    isModalOpen,
    openModal,
    closeModal,
  } = useSmartSearchModal();
  const openModalAndTrack = () => {
    trackEventGA4({
      event_name: 'report_search',
      action: 'modal open',
      type: 'smart search',
      search_from: 'header_search',
    });

    if (isInSmartSearchV1Variation) {
      openV1Modal();
      return;
    }

    openModal();
  };

  useEffect(() => {
    if (!notificationsDisabled) {
      let count = 0;
      const notifications =
        data?.reduce(
          (acc: NormalizedNotification[], notification: Notification) => {
            const normalizedNotification = normalizeNotificationObject(
              notification,
              'sriracha'
            );
            if (
              normalizedNotification.type ===
                'first_unclaimed_money_reminder' &&
              acc.some((n) => n.type === 'first_unclaimed_money_reminder')
            ) {
              return acc;
            }

            const isNotSuggestedReport =
              normalizedNotification.type !== 'suggested_report';
            const isOkToShow =
              isInSuggestedReportsControl || isNotSuggestedReport;

            if (!normalizedNotification.read && isOkToShow) {
              count += 1;
            }
            acc.push(normalizedNotification);
            return acc;
          },
          []
        ) || [];

      setNormalizedNotifications(notifications);
      setUnreadNotificationsCount(count);
    }
  }, [data]);

  let filteredSearchTabs = searchTabs;

  if (isInInternationalLanguageTest) {
    filteredSearchTabs = [];
  }

  if (isInternationalVinTest) {
    filteredSearchTabs = searchTabs.filter((tab) => !tab.isHidden);
  }

  return (
    <HeaderNavContainer className={isInternationalVinTest ? 'vin-nav' : ''}>
      {!isNonCompliant && (
        <>
          {/**
           * TODO: Use breakpoints to toggle which experience is rendered rather than
           * relying on CSS to conditionally display pre-rendered components - NOH 11/14/23
           */}
          {isInternationalVinTest || isInInternationalLanguageTest
            ? null
            : enableSmartSearch &&
              !shouldHideSearchForm && (
                <MobileSearchInput
                  toggleIsMounted={
                    isInSmartSearchV1Variation
                      ? openModalAndTrack
                      : toggleIsMounted
                  }
                  smartSearchIcon={
                    isInSmartSearchVariation || isInSmartSearchV1Variation
                  }
                  openSmartSearch={openModalAndTrack}
                />
              )}
          <HeaderNav className="search-nav">
            {!shouldHideSearchForm && filteredSearchTabs.length > 0 && (
              <>
                <HeaderNavItem sx={{ pt: 0 }}>
                  <TabText
                    className="placeholder"
                    onClick={() => {
                      setSelectedTab(filteredSearchTabs[0]);
                    }}
                  >
                    <Icons.Search
                      fontSize="small"
                      sx={{ verticalAlign: 'bottom' }}
                    />{' '}
                    {searchTabsReplacementText ?? 'Search By'}
                  </TabText>
                </HeaderNavItem>
                {!searchTabsReplacementText &&
                  filteredSearchTabs.map((tab) => (
                    <SearchTab
                      tab={tab}
                      setSelectedTab={setSelectedTab}
                      key={tab.id}
                      selectedTab={selectedTab}
                    />
                  ))}
              </>
            )}
            {isInternationalVinTest || isInInternationalLanguageTest
              ? null
              : !shouldHideSearchForm &&
                enableSmartSearchTabInDesktopHeader && (
                  <HeaderNavItem sx={{ pt: 0.375 }}>
                    <Stack direction="row" alignItems="center">
                      <SmartSearchIcon onClick={openModalAndTrack} />
                      <TabText onClick={openModalAndTrack}>
                        Smart Search
                      </TabText>
                    </Stack>

                    {isInSmartSearchVariation && (
                      <SmartSearchModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                      />
                    )}

                    {isInSmartSearchV1Variation && (
                      <SmartSearchV1Modal
                        isOpen={isV1ModalOpen}
                        onClose={closeV1Modal}
                      />
                    )}
                  </HeaderNavItem>
                )}
          </HeaderNav>
        </>
      )}
      <div>
        <HeaderNav className="side-nav">
          {!isNonCompliant && !notificationsDisabled && (
            <>
              {isInInternationalLanguageTest ||
              isInternationalVinTest ? null : (
                <HeaderNavItem
                  className="notification-bell"
                  onClick={handleNotificationBellClick}
                >
                  <NotificationBell unreadCount={unreadNotificationsCount} />
                </HeaderNavItem>
              )}
            </>
          )}
          <HeaderNavItem
            className="menu"
            onClick={handleMenuClick}
            data-cy="header-menu-toggle"
          >
            <TabText className="menu-toggle">
              {isMedium && open && <Icons.Close />}
              {isMedium && !open && <Icons.Menu />}
              {!isMedium && (
                <>
                  <Text component="span">Menu</Text>
                  {open ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
                </>
              )}
            </TabText>
          </HeaderNavItem>
        </HeaderNav>
      </div>
      <HeaderMenu
        anchorEl={menuAnchorEl}
        open={open}
        handleClose={handleCloseMenu}
      />
      <NotificationDropdown
        anchorEl={notificationdropdownAnchorEl}
        open={openNotifications}
        handleClose={handleCloseNotificationDropdown}
        notifications={normalizedNotifications}
        onReadNotification={onReadNotification}
        onReadAllNotifications={onReadAllNotifications}
        onClearNotification={onDeleteNotification}
      />
    </HeaderNavContainer>
  );
}
